import React, { useState, useContext, forwardRef } from 'react';
import { useHistory } from 'react-router';
import { useQuery } from '@apollo/react-hooks';
import Rating from '@mui/material/Rating';

import './mobile-product-detail-review-style.scss';

import { Loading } from '@atoms';
import Button from '@src/componentsNew/Buttons/Button';
import Typography from '@src/componentsNew/Typography/Typography';
import ProductListTitle from '@src/componentsNew/ProductListTitle/ProductListTitle';
import MobileReviewListItem from '@src/componentsNew/ListItem/ReviewListItem/MobileReviewListItem';
import MobileReviewWriteModal from '@src/componentsNew/Modals/ReviewWriteModal/MobileReviewWriteModal';
import MobileTotalReviewModal from '@src/componentsNew/Modals/TotalReviewModal/MobileTotalReviewModal';
import SectionDivider from '@src/componentsNew/SectionDivider';

import StarIcon from '@src/componentsNew/Icons/Icons/StarIcon';
import PendingIcon from '@src/componentsNew/Icons/Icons/PendingIcon';
import InfoIcon from '@src/componentsNew/Icons/Icons/InfoIcon';

import { OrderDirection, ReviewSortField } from '__generated__/globalTypes';
import {
  product_product_variants,
  product_product_productReviewsByUser,
} from '@components/ProductPage/ProductDetailPage/__generated__/product';
import { REVIEWS } from '@molecules/ReviewList/queries';
import { reviews, reviewsVariables } from '@molecules/ReviewList/__generated__/reviews';

import { CredentialContext, credentialType } from '@providers';
import { redirectToLoginWithCurrentPath } from '@src/utils/authCheck';

export interface IProps {
  productId: string;
  productRefetch?: any;
  variants: (product_product_variants | null)[];
  showVariants?: boolean;
  recentReview?: product_product_productReviewsByUser | null;
  isUserBlockedProductReview?: boolean;
}
const reviewsToShow = 5;

const MobileProductDetailReview = forwardRef<HTMLDivElement, IProps>(
  ({ productId, productRefetch, variants, showVariants, recentReview, isUserBlockedProductReview }, ref) => {
    const history = useHistory();
    const { currentUser } = useContext<credentialType>(CredentialContext);

    const initialVariables: reviewsVariables = {
      product: productId,
      first: reviewsToShow,
      sortBy: {
        field: ReviewSortField.CREATE_AT,
        direction: OrderDirection.DESC,
      },
    };

    const [isReviewExist, setIsReviewExist] = useState(false);
    const [isReviewTotalCountOverToShow, setIsReviewTotalCountOverToShow] = useState(false);
    const [isReviewWriteModalOpen, setIsReviewWriteModalOpen] = useState(false);
    const [isTotalReviewModalOpen, setIsTotalReviewModalOpen] = useState(false);

    const { data: reviewsData, loading, refetch } = useQuery<reviews>(REVIEWS, {
      variables: initialVariables,
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const reviews = data.reviews;

        setIsReviewExist(reviews.totalCount > 0);
        setIsReviewTotalCountOverToShow(reviews.totalCount > reviewsToShow);
      },
    });

    const handleReviewWriteModalClose = () => {
      setIsReviewWriteModalOpen(false);
    };

    const handleReviewWriteModalOpen = () => {
      setIsReviewWriteModalOpen(true);
    };

    const handleTotalReviewModalClose = () => {
      setIsTotalReviewModalOpen(false);
    };

    const handleTotalReviewModalOpen = () => {
      setIsTotalReviewModalOpen(true);
    };

    const handleClickWriteReviewButton = () => {
      if (currentUser?.token) {
        handleReviewWriteModalOpen();
      } else {
        redirectToLoginWithCurrentPath(history);
      }
    };

    if (loading) {
      return <Loading />;
    }

    return (
      <div id="mobile-detail-product-detail-review-area" ref={ref}>
        <ProductListTitle title="Reviews & Ratings" typoVariant="subtitle2" typoComponent="h6" />

        {isReviewExist ? (
          <div className="review-count-wrapper">
            <Rating max={5} value={5} icon={<StarIcon fillColor="#212121" />} readOnly />

            <Typography cName="variant-text" variant="body3" component="span" style={{ color: '#666666' }}>
              {`${reviewsData?.reviews?.totalCount} Reviews`}
            </Typography>
          </div>
        ) : null}

        {!isUserBlockedProductReview ? (
          <>
            {(!recentReview || recentReview?.status === 'REJECTED') && (
              <div className="write-review-btn-wrapper">
                <div className="review-reward-point-text-wrapper">
                  <InfoIcon />

                  <Typography variant="caption" component="p" style={{ color: '#666666' }}>
                    Currently, points not being awarded for writing reviews
                  </Typography>
                </div>

                <Button cName="write-review-btn" size="medium" onClick={handleClickWriteReviewButton}>
                  Write Review
                </Button>
              </div>
            )}

            {recentReview?.status === 'PENDING' && (
              <div className="pending-text-wrapper">
                <PendingIcon />
                <Typography cName="pending-text" variant="subtitle2" component="span" style={{ color: '#4CAF50' }}>
                  Your review is currently under review
                </Typography>
              </div>
            )}

            {!isReviewExist && (
              <div className="no-review-text-wrapper">
                <Typography cName="no-review-text" variant="body3" component="p" style={{ color: '#666666' }}>
                  No Reviews Yet
                </Typography>
              </div>
            )}
          </>
        ) : !isReviewExist ? (
          <div className="no-review-text-wrapper">
            <Typography cName="no-review-text" variant="body3" component="p" style={{ color: '#666666' }}>
              No Reviews Yet
            </Typography>
          </div>
        ) : null}

        {isReviewExist && (
          <div className="product-detail-review-item-wrapper">
            {reviewsData?.reviews?.edges.slice(0, reviewsToShow).map((edge) => (
              <MobileReviewListItem
                key={edge.node.id}
                review={edge.node}
                refetch={refetch}
                showCommentSimple={isReviewTotalCountOverToShow}
                onClick={isReviewTotalCountOverToShow ? handleTotalReviewModalOpen : null}
              />
            ))}

            {reviewsData?.reviews?.totalCount > reviewsToShow && (
              <>
                <Button size="small" cName="see-all-review-btn" variant="text" onClick={handleTotalReviewModalOpen}>
                  See All Reviews
                </Button>
                <div className="blur-overlay" />
              </>
            )}
          </div>
        )}

        <SectionDivider />

        {isReviewWriteModalOpen && (
          <MobileReviewWriteModal
            open={isReviewWriteModalOpen}
            onClose={handleReviewWriteModalClose}
            productId={productId}
            productRefetch={productRefetch}
            variants={variants}
            showVariants={showVariants}
          />
        )}

        {isTotalReviewModalOpen && (
          <MobileTotalReviewModal
            open={isTotalReviewModalOpen}
            onClose={handleTotalReviewModalClose}
            initialVariables={initialVariables}
          />
        )}
      </div>
    );
  },
);

export default MobileProductDetailReview;
